import useAuthStore from '@/store/modules/auth.js';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useLocaleStore from '@/store/modules/locale.js';
import useRegisterStore from '@/store/modules/register.js';
import useBonusPhaseStore from '@/store/modules/bonusPhase.js';
import useThemeHook from '@/hooks/useTheme.js';
import useForgotPasswordHook from '@/hooks/useForgotPassword.js';
import useRegisterHook from '@/hooks/useRegister.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import usePreGameHook from '@/hooks/usePreGame.js';
import { routeIndexGroupNames } from '@/constants/route.js';

export const beforeEachRouteNavigationGuard = (to, _from, next) => {
  const authStore = useAuthStore();
  const isAuthRoute = to.matched.some(record => record.meta.requiresAuth);

  let nextRoute;

  if (isAuthRoute && !authStore.isUserAuthenticated) {
    nextRoute = { name: routeIndexGroupNames.VISITOR, query: to.query };
  } else if (!isAuthRoute && authStore.isUserAuthenticated) {
    nextRoute = { name: routeIndexGroupNames.AUTH, query: to.query };
  }

  next(nextRoute);
};

export const mainNavigationGuard = async (to, _from, next) => {
  const authStore = useAuthStore();
  const localeStore = useLocaleStore();

  const { loadCssFiles } = useThemeHook();

  const {
    lang: langQueryParam,
    user_auth_token: tokenQueryParam,
    checkId: checkIdQueryParam,
    email: emailQueryParam,
    ...restQueryParams
  } = to.query;

  if (
    langQueryParam &&
    langQueryParam !== localeStore.currentLanguage &&
    Object.hasOwn(localeStore.languages, langQueryParam)
  ) {
    localeStore.setCurrentLanguage(langQueryParam);

    await localeStore.dispatchRetrieveTranslations();

    await loadCssFiles();

    next({
      name: to.name,
      query: { user_auth_token: tokenQueryParam, ...restQueryParams },
      replace: true,
    });

    return;
  }

  if (tokenQueryParam) {
    let nextRouteName;

    try {
      await authStore.dispatchAutoLogin({ token: tokenQueryParam });

      nextRouteName = routeIndexGroupNames.AUTH;
    } catch {
      nextRouteName = routeIndexGroupNames.VISITOR;
    }

    next({
      name: nextRouteName,
      query: restQueryParams,
      replace: true,
    });

    return;
  }

  if (checkIdQueryParam || emailQueryParam) {
    authStore.setUrlParams({ checkId: checkIdQueryParam, email: emailQueryParam });

    next({
      name: to.name,
      query: restQueryParams,
      replace: true,
    });

    return;
  }

  next();
};

export const visitorLayoutNavigationGuard = (to, _from, next) => {
  const registerStore = useRegisterStore();

  if (Object.keys(to.query).length > 0) {
    registerStore.setUrlParams(to.query);
  }

  next();
};

export const registerNavigationGuard = (to, _from, next) => {
  const registerStore = useRegisterStore();
  const { indexRouteName } = usePageNavigationHook();
  const { stepConfiguration } = useRegisterHook(to.name);

  if (!stepConfiguration.value?.supported) {
    next({ name: stepConfiguration.value?.fallbackRouteName || indexRouteName.value });
  } else {
    registerStore.setUrlParams(to.query);

    next();
  }
};

export const forgotPasswordNavigationGuard = (to, _from, next) => {
  const { indexRouteName } = usePageNavigationHook();
  const { stepConfiguration } = useForgotPasswordHook(to.name);

  if (!stepConfiguration.value?.supported) {
    next({ name: stepConfiguration.value?.fallbackRouteName || indexRouteName.value });
  } else {
    next();
  }
};

export const storeNavigationGuard = (_to, _from, next) => {
  const appSettings = useAppSettingsStore();

  const { indexRouteName } = usePageNavigationHook();

  if (appSettings.isStoreDisabled) {
    next({ name: indexRouteName.value });
  } else {
    next();
  }
};

export const leaderboardNavigationGuard = (_to, _from, next) => {
  const appSettings = useAppSettingsStore();

  const { indexRouteName } = usePageNavigationHook();

  if (appSettings.isLeaderboardDisabled) {
    next({ name: indexRouteName.value });
  } else {
    next();
  }
};

export const winnersNavigationGuard = (_to, _from, next) => {
  const appSettings = useAppSettingsStore();

  const { indexRouteName } = usePageNavigationHook();

  if (appSettings.isWinnersDisabled) {
    next({ name: indexRouteName.value });
  } else {
    next();
  }
};

export const prizesNavigationGuard = (_to, _from, next) => {
  const appSettings = useAppSettingsStore();

  const { indexRouteName } = usePageNavigationHook();

  if (appSettings.isPrizesPageDisabled) {
    next({ name: indexRouteName.value });
  } else {
    next();
  }
};

export const selectCategoryGameNavigationGuard = (_to, _from, next) => {
  const { isGameThemeSelectCategory } = usePreGameHook();
  const { indexRouteName } = usePageNavigationHook();

  if (!isGameThemeSelectCategory.value) {
    next({ name: indexRouteName.value });
  } else {
    next();
  }
};

export const bonusGameNavigationGuard = (_to, _from, next) => {
  const bonusPhaseStore = useBonusPhaseStore();
  const { indexRouteName } = usePageNavigationHook();

  if (!bonusPhaseStore.bonusPhaseGameConfig.gameId) {
    next({ name: indexRouteName.value });
  } else {
    next();
  }
};
